/**
 * Auth-Guard — Token + UserContext-Probe (Phase 3.5 §3, wired mit Router §4).
 *
 * Pattern:
 *   - `probeAuth()`: returnt UserContext oder null. Kein Redirect. Genutzt vom
 *     Bootstrap in main.ts um zu entscheiden "Login-Page oder App rendern".
 *   - `requireAuth()`: returnt UserContext oder leitet zu Login. Genutzt von
 *     Router-Routes als ersten Schritt einer guarded Render-Function.
 *
 * Drift zu `getUserContext()` aus api/client.ts:
 *   - getUserContext() macht Auto-Redirect bei 401 (für API-Calls mid-session)
 *   - probeAuth() macht KEINEN Redirect (für initial Bootstrap-Check, sonst
 *     Loop zwischen Login-Page und Google)
 */

import { WORKER_URL } from "../api/client";
import type { ApiResponseError, ApiResponseOk, UserContext } from "../api/types";
import { clearToken, getToken } from "./token";
import { redirectToLogin } from "./login";

/**
 * Ergebnis der Anmelde-Prüfung — DREI Ausgänge, nicht zwei.
 *
 * ═══ 🔴 DIE LOGIN-SCHLEIFE VOM 29.10.2026 ═══════════════════════════════════
 * 📏 07:56–08:23 UTC war die Prod-D1 weg. `/me` antwortete 403 bzw. 500, und
 * bis 02.11. kannte diese Prüfung nur „User“ oder „null“. Jede Fehlerantwort
 * wurde so zu „nicht angemeldet“ ⇒ der Start warf die Anmeldung weg ⇒
 * Anmeldemaske für alle, immer wieder.
 *
 * 🔑 „Gestört“ heisst: der Server konnte es gerade nicht sagen. Das ist kein
 * Urteil über die Anmeldung, also bleibt sie liegen.
 * Entscheid Justin 02.11.: gestört = 5xx, keine Verbindung, unlesbare Antwort.
 */
export type AuthProbe =
  | { art: "angemeldet"; user: UserContext }
  /** Kein Token, 401 (Token gelöscht) oder 403 (kein Profil — Token bleibt für den Claim-Flow). */
  | { art: "abgemeldet" }
  /** Server gestört — Anmeldung NICHT anfassen, Störungsseite zeigen. */
  | { art: "gestoert"; grund: string };

/**
 * Prüft den aktuellen Token und lädt den UserContext.
 *
 * - Kein Token → abgemeldet
 * - 401 → Token clearen + abgemeldet
 * - 5xx · keine Verbindung · Antwort ohne JSON → gestoert (Token bleibt)
 * - 403 o. a. Fehler-Envelope → abgemeldet (Token bleibt, Claim-Flow braucht ihn)
 * - 200 → angemeldet
 */
export async function probeAuthStatus(): Promise<AuthProbe> {
  const token = getToken();
  if (!token) return { art: "abgemeldet" };

  let res: Response;
  try {
    res = await fetch(`${WORKER_URL}/api/v1/auth/me`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    console.warn(`[probeAuth] Network-Fail: ${(err as Error).message}`);
    return { art: "gestoert", grund: "keine Verbindung zum Server" };
  }

  if (res.status === 401) {
    clearToken();
    return { art: "abgemeldet" };
  }

  // 🔴 VOR dem JSON-Lesen: eine 5xx-Antwort ist eine Störung, gleich was im Körper steht.
  if (res.status >= 500) {
    return { art: "gestoert", grund: `Server antwortet mit ${res.status}` };
  }

  let data: ApiResponseOk<UserContext> | ApiResponseError;
  try {
    data = (await res.json()) as ApiResponseOk<UserContext> | ApiResponseError;
  } catch {
    // z. B. Pages ohne /api-Proxy liefert die App-Seite statt JSON.
    return { art: "gestoert", grund: "unlesbare Antwort vom Server" };
  }

  if (data.status === "error") {
    // 403 = Token gültig (Google-verifiziert), aber kein MA-Profil. Token NICHT
    // clearen — der Bootstrap-Claim-Flow (Phase 7.7) braucht ihn für die
    // Profil-Selbstzuordnung. Bei echtem Abbruch clear't der Bootstrap selbst.
    return { art: "abgemeldet" };
  }

  return { art: "angemeldet", user: data.result };
}

/**
 * Kurzform: UserContext oder null. Eine Störung ist hier ebenfalls `null` —
 * wer zwischen „abgemeldet“ und „gestört“ unterscheiden muss (der Start in
 * `main.ts`), nimmt `probeAuthStatus`.
 */
export async function probeAuth(): Promise<UserContext | null> {
  const p = await probeAuthStatus();
  return p.art === "angemeldet" ? p.user : null;
}

/**
 * Garantiert UserContext oder leitet zu Google-Login + wirft.
 * Genutzt am Anfang jeder Router-Route die Auth braucht.
 *
 * Aufrufer fängt den Throw nicht — Redirect ist Hard-Navigation,
 * Code danach läuft eh nicht mehr.
 * 🔴 Bei einer Störung KEIN Redirect (sonst Login-Schleife, 29.10.) — nur der Wurf.
 */
export async function requireAuth(): Promise<UserContext> {
  const p = await probeAuthStatus();
  if (p.art === "angemeldet") return p.user;
  if (p.art === "gestoert") throw new Error("AUTH_GESTOERT");
  redirectToLogin();
  throw new Error("AUTH_REQUIRED");
}
